import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { getToken } from '../services/authService';
import { useCurrency } from '../context/CurrencyContext';

export default function Profile() {
  const navigate = useNavigate();
  const { currency, rates } = useCurrency();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate('/login');
    }
  }, [navigate]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);

  const currencySymbol = (code) => {
    const c = String(code || '').toUpperCase();
    if (c === 'EUR') return '€';
    if (c === 'USD') return '$';
    if (c === 'ALL') return 'L';
    return c;
  };

  const toAllAmount = (amount, orderCurrency, exchangeRate) => {
    const amt = Number(amount || 0);
    const cur = String(orderCurrency || 'ALL').toUpperCase();
    const rate = Number(exchangeRate || 0);
    if (cur !== 'ALL' && Number.isFinite(rate) && rate > 0) return amt * rate;
    return amt;
  };

  const formatMoney = (amountAll) => {
    const rate = Number(rates?.[currency] || 1);
    const display = rate > 0 ? Number(amountAll || 0) / rate : Number(amountAll || 0);
    return `${currencySymbol(currency)}${display.toFixed(2)} ${currency}`;
  };

  const resolveProductImage = (img) => {
    if (!img) return '';
    if (String(img).startsWith('http')) return img;
    return `${API_BASE_URL.replace(/\/api\/?$/, '')}${img}`;
  };

  // Form states
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('ALL');
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSMS, setMarketingSMS] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchOrders();
  }, []);

  async function fetchProfile() {
    setLoading(true);
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_BASE_URL}/users/me`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        // Initialize form
        setUsername(data.username || '');
        setName(data.name || '');
        setSurname(data.surname || '');
        setEmail(data.email || '');
        setPhoneNumber(data.phoneNumber || '');
        setStreetAddress(data.streetAddress || '');
        setCity(data.city || '');
        setStateProvince(data.stateProvince || '');
        setZip(data.zip || '');
        setCountry(data.country || '');
        // handle date formatting if needed (assuming ISO string from backend)
        setDateOfBirth(data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '');
        setGender(data.gender || '');
        setPreferredCurrency(data.preferredCurrency || 'ALL');
        setMarketingEmail(!!data.marketingEmail);
        setMarketingSMS(!!data.marketingSMS);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOrders() {
    setOrdersLoading(true);
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_BASE_URL}/orders/me`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrders(Array.isArray(data) ? data : []);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error(err);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const token = getToken();
      const fd = new FormData();
      // fd.append('username', username);
      fd.append('name', name);
      fd.append('surname', surname);
      fd.append('email', email);
      fd.append('phoneNumber', phoneNumber);
      fd.append('streetAddress', streetAddress);
      fd.append('city', city);
      fd.append('stateProvince', stateProvince);
      fd.append('zip', zip);
      fd.append('country', country);
      if (dateOfBirth) fd.append('dateOfBirth', dateOfBirth);
      if (gender) fd.append('gender', gender);
      fd.append('preferredCurrency', preferredCurrency);
      fd.append('marketingEmail', marketingEmail);
      fd.append('marketingSMS', marketingSMS);

      if (file) fd.append('profileImage', file);

      const res = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json();
        setMessage({ type: 'error', text: err.message || 'Failed to save profile' });
      } else {
        const updated = await res.json();
        setUser(updated);
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        setFile(null);
        setIsEditing(false);
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Error saving profile' });
    } finally {
      setSaving(false);
    }
  }

  const cancelEdit = () => {
    fetchProfile();
    setIsEditing(false);
    setFile(null);
  };

  if (loading) return <div className="page-container" style={{ padding: 40, textAlign: 'center' }}>Loading profile...</div>;
  if (!user) return <div className="page-container" style={{ padding: 40, textAlign: 'center' }}>Please log in to view your profile.</div>;

  const sectionStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0b79d0',
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '6px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelValueStyle = {
    fontSize: '14px',
    color: '#333',
    padding: '10px 0',
    minHeight: '41px',
    display: 'flex',
    alignItems: 'center'
  };

  return (
    <div className="page-container" style={{ maxWidth: 1000, margin: '0 auto', padding: '30px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 30, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '28px', fontWeight: '800', margin: 0 }}>My Profile</h2>
        {message && (
          <div style={{
            padding: '10px 20px',
            borderRadius: '20px',
            background: message.type === 'success' ? '#e6f4ea' : '#fce8e6',
            color: message.type === 'success' ? '#137333' : '#c5221f',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {message.text}
          </div>
        )}
      </div>

      <form onSubmit={handleSave} className="profile-form">

        {/* Left Column: Image & Basic Info */}
        <div>
          <div style={{ ...sectionStyle, textAlign: 'center' }}>
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto 20px',
              background: '#f8f9fa',
              border: '4px solid #fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              {user.profileImage ? (
                <img
                  src={user.profileImage.startsWith('http') ? user.profileImage : `${API_BASE_URL.replace(/\/api$/, '')}${user.profileImage}`}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', color: '#ccc', fontWeight: 'bold' }}>
                  {name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <label style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: '#eef3f8',
              color: '#0b79d0',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '10px'
            }}>
              Change Photo
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {file && isEditing && <div style={{ fontSize: '12px', color: '#666' }}>Selected: {file.name}</div>}

            <div style={{ marginTop: 20, textAlign: 'left', borderTop: '1px solid #eee', paddingTop: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '12px', color: '#888', display: 'block' }}>ACCOUNT TYPE</span>
                <span style={{ fontWeight: '600', textTransform: 'uppercase' }}>{user.role}</span>
              </div>
              <div>
                <span style={{ fontSize: '12px', color: '#888', display: 'block' }}>MEMBER SINCE</span>
                <span style={{ fontWeight: '600' }}>{new Date(user.createdAt || Date.now()).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Edit Form */}
        <div>

          {/* Identity */}
          <div style={sectionStyle}>
            <div style={{ ...titleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Personal Information</span>
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  style={{
                    padding: '6px 12px',
                    background: '#eef3f8',
                    color: '#0b79d0',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Username</label>
                {isEditing ? (
                  <input value={username} disabled style={{ ...inputStyle, background: '#f5f5f5', color: '#999', cursor: 'not-allowed' }} />
                ) : (
                  <div style={labelValueStyle}>{username || '—'}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>First Name</label>
                {isEditing ? (
                  <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{name || '—'}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                {isEditing ? (
                  <input value={surname} onChange={e => setSurname(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{surname || '—'}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Date of Birth</label>
                {isEditing ? (
                  <input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{dateOfBirth || '—'}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Gender</label>
                {isEditing ? (
                  <select value={gender} onChange={e => setGender(e.target.value)} style={{ ...inputStyle, background: '#fff' }}>
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  <div style={labelValueStyle}>{gender || '—'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Contact */}
          <div style={sectionStyle}>
            <div style={titleStyle}>Contact Details</div>
            <div style={gridStyle}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Email Address</label>
                {isEditing ? (
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{email || '—'}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Phone Number</label>
                {isEditing ? (
                  <input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{phoneNumber || '—'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div style={sectionStyle}>
            <div style={titleStyle}>Address</div>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={labelStyle}>Street Address</label>
                {isEditing ? (
                  <input value={streetAddress} onChange={e => setStreetAddress(e.target.value)} style={inputStyle} />
                ) : (
                  <div style={labelValueStyle}>{streetAddress || '—'}</div>
                )}
              </div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>City</label>
                  {isEditing ? (
                    <input value={city} onChange={e => setCity(e.target.value)} style={inputStyle} />
                  ) : (
                    <div style={labelValueStyle}>{city || '—'}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>State / Province</label>
                  {isEditing ? (
                    <input value={stateProvince} onChange={e => setStateProvince(e.target.value)} style={inputStyle} />
                  ) : (
                    <div style={labelValueStyle}>{stateProvince || '—'}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>ZIP / Postal Code</label>
                  {isEditing ? (
                    <input value={zip} onChange={e => setZip(e.target.value)} style={inputStyle} />
                  ) : (
                    <div style={labelValueStyle}>{zip || '—'}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  {isEditing ? (
                    <input value={country} onChange={e => setCountry(e.target.value)} style={inputStyle} />
                  ) : (
                    <div style={labelValueStyle}>{country || '—'}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div style={sectionStyle}>
            <div style={titleStyle}>Preferences</div>
            <div style={{ marginBottom: 15 }}>
              <label style={labelStyle}>Preferred Currency</label>
              {isEditing ? (
                <select value={preferredCurrency} onChange={e => setPreferredCurrency(e.target.value)} style={{ ...inputStyle, maxWidth: '200px', background: '#fff' }}>
                  <option value="ALL">ALL (Lek)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="USD">USD (Dollar)</option>
                </select>
              ) : (
                <div style={labelValueStyle}>{preferredCurrency || 'ALL'}</div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 15 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#444', cursor: 'pointer' }}>
                <input type="checkbox" checked={marketingEmail} disabled={!isEditing} onChange={e => setMarketingEmail(e.target.checked)} style={{ width: 16, height: 16 }} />
                Receive marketing emails
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#444', cursor: 'pointer' }}>
                <input type="checkbox" checked={marketingSMS} disabled={!isEditing} onChange={e => setMarketingSMS(e.target.checked)} style={{ width: 16, height: 16 }} />
                Receive marketing SMS
              </label>
            </div>
          </div>

          {/* Actions */}
          {isEditing && (
            <div className="form-actions">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                style={{ padding: '12px 24px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', color: '#555', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ padding: '12px 30px', background: '#0b79d0', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 6px rgba(11, 121, 208, 0.2)' }}
              >
                {saving ? 'Saving Changes...' : 'Save Changes'}
              </button>
            </div>
          )}

        </div>
      </form>

      <div style={sectionStyle}>
        <div
          style={{ ...titleStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: ordersExpanded ? 20 : 0, borderBottom: ordersExpanded ? '1px solid #eee' : 'none' }}
          onClick={() => setOrdersExpanded(!ordersExpanded)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Order History</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '10px' }}>{orders.length}</span>
          </div>
          <div style={{ fontSize: 16, color: '#64748b' }}>{ordersExpanded ? '−' : '+'}</div>
        </div>

        {ordersExpanded && (
          <div style={{ marginTop: 20 }}>
            {ordersLoading ? (
              <div style={{ color: '#666' }}>Loading orders...</div>
            ) : orders.length === 0 ? (
              <div style={{ color: '#666' }}>No orders found.</div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                {orders.map((o) => (
                  <div key={o.id} style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: 16, background: '#fff', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>Order #{o.id}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{o.createdAt ? new Date(o.createdAt).toLocaleString() : '—'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {((o.items && o.items.some(it => it.hasTradeIn)) || (o.adminNote && o.adminNote.includes('Sill të vjetrën')) || (o.address && o.address.includes('Sill të vjetrën'))) && (
                          <span style={{ padding: '4px 10px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 700 }} title="Fushata: Sill të vjetrën merr të renë">
                            ♻️ Me pajisje të vjetër
                          </span>
                        )}
                        <span style={{ padding: '4px 10px', borderRadius: 999, background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{o.status || '—'}</span>
                        <span style={{ padding: '4px 10px', borderRadius: 999, background: '#ecfeff', color: '#0e7490', fontSize: 12, fontWeight: 700 }}>
                          {formatMoney(toAllAmount(o.total, o.currency, o.exchangeRate))}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, display: 'grid', gap: 6, color: '#475569', fontSize: 13 }}>
                      <div>Payment: <strong>{o.paymentMethod || '—'}</strong></div>
                      <div>Shipping: <strong>{[o.address, o.city, o.region, o.zip, o.country].filter(Boolean).join(', ') || '—'}</strong></div>
                    </div>
                    {Array.isArray(o.items) && o.items.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Items</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {o.items.map((it) => {
                            const img = it.product?.image || (Array.isArray(it.product?.images) ? it.product.images[0] : '') || it.image || '';
                            const imgUrl = resolveProductImage(img);
                            return (
                              <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 10, borderRadius: 12, background: '#f8fafc', border: '1px solid #eef2f7' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fff', overflow: 'hidden', flexShrink: 0, border: '1px solid #e2e8f0' }}>
                                    {imgUrl ? (
                                      <img src={imgUrl} alt={it.product?.name || 'Item'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : null}
                                  </div>
                                  <div style={{ color: '#0f172a', fontWeight: 600 }}>
                                    {it.product?.name || it.productName || 'Item'}
                                    {it.isBundle && it.bundleItems && it.bundleItems.length ? (
                                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', fontWeight: 'normal' }}>
                                        Pako përmban: {it.bundleItems.map(c => `${c.name} ${c.side === 'left' ? '(Kryesor)' : '(Shtesë)'}`).join(', ')}
                                      </div>
                                    ) : null}
                                    {it.hasTradeIn && (
                                      <div style={{ fontSize: '11px', color: '#15803d', marginTop: '3px', fontWeight: 600 }}>
                                        ♻️ Dorëzim pajisjeje të vjetër (Fushata "Sill të vjetrën merr të renë")
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div style={{ color: '#475569', fontWeight: 600 }}>x{it.quantity} · {formatMoney(toAllAmount(it.price, 'ALL', 1))}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
